/* eslint-disable @typescript-eslint/no-unused-vars */
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { GetDeliveryOrderByIdObject } from "../../../@types/DRO/DeliveryOrderResponse";
import { DeliveryOrderApi } from "../../../api/DeliveryOrder/DeliveryOrderApi";
import { DownloadPdfApi } from "../../../api/PdfDownload/DownloadPdfApi";
import { MEDIA_BASE_URL } from "../../../config/apiConfig";
import CreateDeliveryOrderDraft from "../CreateOrder/CreateDeliveryOrderDraft";
import { DeliveryOrderHistory } from "../OrderHistory/DeliveryOrderHistory";
interface ViewDeliveryOrderProps {
  onBack: () => void;
  orderId: number;
}

const ViewDeliveryOrder: React.FC<ViewDeliveryOrderProps> = ({
  onBack,
  orderId,
}) => {
  const [orderDetails, setSingleOrderDetails] =
    useState<GetDeliveryOrderByIdObject>();

  const singleOrderData = useCallback(async () => {
    if (!orderId) return null;

    try {
      const response = await DeliveryOrderApi.api.getSingleDeliverOrderDetails(
        orderId
      );

      if (response?.data) {
        setSingleOrderDetails(response.data);
      }
    } catch (error) {
      console.error("Error fetching order details", error);
    }
  }, [orderId]);

  useEffect(() => {
    singleOrderData();
  }, [singleOrderData]);

  const parsedItems =
    orderDetails?.deliveryOrderLines?.map((item) => {
      const partName = item?.partName ?? "N/A";
      const quantityOrdered = item?.quantityOrdered ?? 0;
      const price = item?.cost ?? 0;
      const quantityReceived = item?.quantityReceived ?? 0;
      const quantityReturned = item?.quantityReturned ?? 0;
      const total = item?.total ?? 0;
      const tax = item?.tax ?? null;

      const formattedPrice =
        "₹" + price.toLocaleString("en-IN", { minimumFractionDigits: 2 });
      const formattedTotal =
        "₹" + total.toLocaleString("en-IN", { minimumFractionDigits: 2 });

      return {
        description: partName,
        referenceCode: item?.referenceCode ?? "N/A",
        demand: item.demand.toString(),
        quantity: quantityOrdered.toString(),
        received: quantityReceived.toString(),
        returned: quantityReturned.toString(),
        unit: "Units",
        price: formattedPrice,
        taxes: tax !== null && tax !== undefined ? `${tax} %` : "N/A",
        total: formattedTotal,
      };
    }) ?? [];

  const [returnToVendor, setReturnToVendor] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const showErrorMessage = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  };

  const handleDownloadPdf = async () => {
    if (!orderId) {
      showErrorMessage("Order ID is required to download PDF");
      return;
    }

    try {
      setIsDownloading(true);
      await DownloadPdfApi.api.getPdfLinkByDOId(orderId);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      showErrorMessage("Failed to download PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  function handleReturn() {
    setReturnToVendor(true);
  }

  return returnToVendor ? (
    <CreateDeliveryOrderDraft
      onBack={onBack}
      orderStage="RETURNED"
      orderDataId={orderId}
    />
  ) : (
    <Box sx={{ m: 4 }}>
      <Paper
        sx={{
          p: 4,
          borderRadius: "12px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          backgroundColor: "#fff",
        }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              textAlign: "left",
              fontWeight: "600",
              color: "#1E1E1E",
            }}>
            View Delivery Order
          </Typography>
          {orderId && (
            <Button
              variant="contained"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              sx={{
                backgroundColor: "#1366D9",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
                padding: "8px 16px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "#0f5cc0",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "&:disabled": {
                  backgroundColor: "#B0C4DE",
                },
              }}>
              {isDownloading ? "Downloading..." : "Download PDF"}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 4,
            backgroundColor: "#F8F9FA",
            padding: "12px 16px",
            borderRadius: "8px",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          }}>
          <Button
            onClick={onBack}
            sx={{
              textTransform: "none",
              color: "#646464",
              p: 0,
              "&:hover": { color: "#1E1E1E", backgroundColor: "transparent" },
            }}>
            Dashboard
          </Button>
          <Typography variant="body2" color="#646464" sx={{ mx: 1 }}>
            &gt;
          </Typography>
          <Button
            onClick={onBack}
            sx={{
              textTransform: "none",
              color: "#646464",
              p: 0,
              "&:hover": { color: "#1E1E1E", backgroundColor: "transparent" },
            }}>
            Orders
          </Button>
          <Typography variant="body2" color="#646464" sx={{ mx: 1 }}>
            &gt;
          </Typography>
          <Typography
            variant="body2"
            color="#1E1E1E"
            sx={{ fontWeight: "500" }}>
            View Order
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Order #{orderDetails?.id ?? "-"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryDialogOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 500,
              }}>
              History
            </Button>

            {orderDetails?.status && (
              <Box
                sx={{
                  backgroundColor:
                    orderDetails?.status.name === "COMPLETED"
                      ? "#4CD964"
                      : "#F0AD4E",
                  color: "#fff",
                  px: 2,
                  py: "4px",
                  borderRadius: "9999px",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}>
                {orderDetails?.status.displayName}
              </Box>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Destination Location
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.deliveryWareHouseDetails?.deliveryWarehouseName}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              PO Id
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.purchaseOrderId}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Scheduled Date
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.scheduledDate || "-"}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Vendor
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.vendorWarehouseDto?.vendorName}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Source DO
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.parentId || "-"}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Invoice Id 
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.invoiceId || "-"}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Docket Number
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.docketNumber || "-"}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Courier Name
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.courierName || "-"}
            </Box>
          </Grid>
          {orderDetails?.attachment && (
            <Grid item xs={12} sm={4}>
            <Box
              sx={{
                my: "1rem",
                textAlign: "start",
              }}>
              <Button
                  component="a"
                  variant="contained"
                  href={`${MEDIA_BASE_URL}/${orderDetails.attachment}`}
                  download
                  target="_blank"
                  rel="noreferrer"
                  startIcon={<DownloadIcon />}
                  sx={{
                    minWidth: 190,
                    height: 42,
                    justifyContent: "center",
                    px: 2,
                    textTransform: "none",
                    borderRadius: "999px",
                    background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                    fontWeight: 600,
                    boxShadow: "0 6px 14px rgba(79, 70, 229, 0.22)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1d4ed8, #6d28d9)",
                      boxShadow: "0 8px 18px rgba(79, 70, 229, 0.3)",
                    },
                  }}>
                Download Attachment
              </Button>
            </Box>
          </Grid>
          )}
        </Grid>

        <Box mt={4}>
          <Typography
            variant="body1"
            gutterBottom
            sx={{ fontWeight: 600, display: "flex" }}>
            Order Items
          </Typography>
          <Paper
            elevation={1}
            sx={{
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
            }}>
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead sx={{ backgroundColor: "#F1F3F5" }}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 600, width: 40, textAlign: "center" }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 220 }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 150 }}>
                      Reference Code
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 90,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Quantity Ordered
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 90,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Quantity Received
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 90,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Quantity Returned
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: 70, textAlign: "center" }}>
                      Unit
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 100,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Price
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 70,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Tax
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 110,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedItems.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#fff" : "#F8F9FB",
                        "&:hover": { backgroundColor: "#FAFAFA" },
                      }}>
                      <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: 14 }}>
                        {item.description}
                      </TableCell>
                      <TableCell sx={{ fontSize: 14 }}>
                        {item.referenceCode}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.quantity}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.received}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.returned}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                        {item.unit}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.price}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.taxes}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.total}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Show Total Amount below the table */}
            <Box sx={{ mt: 2, mr: 2, textAlign: "right" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "600", color: "#1E1E1E" }}>
                Total Amount: ₹{" "}
                {orderDetails?.totalAmount !== undefined && orderDetails?.totalAmount !== null
                  ? Number(orderDetails.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })
                  : "0.00"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}>
              <Button
                variant="contained"
                color="error"
                sx={{
                  marginRight: 2,
                  marginBottom: 2,
                  padding: "8px 20px",
                  borderRadius: "32px",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                }}
                onClick={onBack}>
                Close Order
              </Button>
              <Button
                onClick={handleReturn}
                variant="contained"
                color="primary"
                sx={{
                  padding: "8px 20px",
                  marginBottom: 2,
                  borderRadius: "32px",
                  backgroundColor: "#1E1E1E",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  "&:hover": { backgroundColor: "#000000" },
                }}>
                Return To Vendor
              </Button>
            </Box>
          </Paper>
        </Box>
      </Paper>

      <DeliveryOrderHistory
        deliveryOrderId={orderId}
        variant="dialog"
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
      />
    </Box>
  );
};

export default ViewDeliveryOrder;
